import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Stack,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  X,
  Settings,
  RefreshCw,
} from 'lucide-react';
import { rabbitmqApi } from '../../../../services/rabbitmqServices/rabbitmqService';

export const SettingsModal = ({
  isOpen = false,
  onClose = () => {},
  onSettingsSaved = () => {},
}) => {
  const [formData, setFormData] = useState({
    serverUrl: '',
    username: '',
    password: '',
    vhost: '/',
    queueName: '',
    clusterName: '',
    refreshInterval: 5,
    useSimulation: false,
  });

  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const [testStatus, setTestStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchQueues = async (vhostToUse) => {
    setLoadingQueues(true);
    try {
      const vh = vhostToUse !== undefined ? vhostToUse : formData.vhost || '/';
      const list = await rabbitmqApi.getQueues(vh);
      if (Array.isArray(list)) {
        setQueues(list);
      }
    } catch (e) {
      console.error('Failed to fetch queues:', e);
    } finally {
      setLoadingQueues(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      rabbitmqApi.getSettings()
        .then((data) => {
          if (data) {
            setFormData({
              serverUrl: data.serverUrl || '',
              username: data.username || '',
              password: data.password || '',
              vhost: data.vhost || '/',
              queueName: data.queueName || '',
              clusterName: data.clusterName || '',
              refreshInterval: data.refreshInterval || 5,
              useSimulation: Boolean(data.useSimulation),
            });
            fetchQueues(data.vhost || '/');
          }
        })
        .catch((e) => console.error(e));
    }
  }, [isOpen]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestStatus(null);
    try {
      const data = await rabbitmqApi.testConnection({
        serverUrl: formData.serverUrl,
        username: formData.username,
        password: formData.password,
      });
      if (data.success) {
        setTestStatus({
          tested: true,
          success: true,
          message: `Connected successfully! Authenticated as ${data.user || formData.username}`,
        });
        if (Array.isArray(data.queues) && data.queues.length > 0) {
          setQueues(data.queues);
          if (!formData.queueName || !data.queues.some((q) => q.name === formData.queueName)) {
            setFormData((prev) => ({ ...prev, queueName: data.queues[0].name }));
          }
        }
      } else {
        setTestStatus({
          tested: true,
          success: false,
          message: data.message || `HTTP ${data.status}: Authentication failed or server unreachable`,
        });
      }
    } catch (err) {
      setTestStatus({
        tested: true,
        success: false,
        message: err.message || 'Network error connecting to backend',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await rabbitmqApi.updateSettings(formData);
      onSettingsSaved(formData);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Settings style={{ width: 20, height: 20, color: '#006951' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            RabbitMQ Connection Settings
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <X style={{ width: 18, height: 18 }} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSave}>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Server URL */}
            <TextField
              label="RabbitMQ Management Server URL"
              fullWidth
              size="small"
              required
              value={formData.serverUrl}
              onChange={(e) => setFormData({ ...formData, serverUrl: e.target.value })}
              placeholder="http://localhost:15672"
              helperText="The HTTP Management API endpoint (port 15672 or custom proxy)"
            />

            {/* Credentials */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="guest"
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                size="small"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
              />
            </Stack>

            {/* VHost & Queue Name */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Virtual Host"
                fullWidth
                size="small"
                required
                value={formData.vhost}
                onChange={(e) => {
                  const nextVhost = e.target.value;
                  setFormData({ ...formData, vhost: nextVhost });
                  fetchQueues(nextVhost);
                }}
                placeholder="/"
              />

              {manualMode ? (
                <TextField
                  label="Target Queue Name"
                  fullWidth
                  size="small"
                  required
                  value={formData.queueName}
                  onChange={(e) => setFormData({ ...formData, queueName: e.target.value })}
                  placeholder="my-queue"
                  helperText={
                    <Button size="small" onClick={() => setManualMode(false)} sx={{ fontSize: '0.65rem', p: 0 }}>
                      Select from list
                    </Button>
                  }
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="queue-select-label">Target Queue Name</InputLabel>
                  <Select
                    labelId="queue-select-label"
                    label="Target Queue Name"
                    value={formData.queueName}
                    onChange={(e) => setFormData({ ...formData, queueName: e.target.value })}
                  >
                    {formData.queueName && !queues.some((q) => q.name === formData.queueName) && (
                      <MenuItem value={formData.queueName}>{formData.queueName} (current)</MenuItem>
                    )}
                    {queues.map((q, idx) => (
                      <MenuItem key={`${q.vhost || '/'}-${q.name}-${idx}`} value={q.name}>
                        {q.name} ({q.messages} msgs)
                      </MenuItem>
                    ))}
                  </Select>
                  <Stack direction="row" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {queues.length} queue{queues.length === 1 ? '' : 's'} available
                    </Typography>
                    <Button size="small" onClick={() => setManualMode(true)} sx={{ fontSize: '0.65rem', p: 0 }}>
                      Type custom
                    </Button>
                  </Stack>
                </FormControl>
              )}
            </Stack>

            {/* Test Connection Result */}
            {testStatus && (
              <Alert severity={testStatus.success ? 'success' : 'warning'} sx={{ borderRadius: 2 }}>
                <strong>{testStatus.success ? 'Connection Valid: ' : 'Notice: '}</strong>
                {testStatus.message}
              </Alert>
            )}

            {/* Test Connection Button */}
            <Button
              variant="outlined"
              fullWidth
              onClick={handleTestConnection}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={14} /> : <RefreshCw style={{ width: 14, height: 14 }} />}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              {testing ? 'Testing Connection...' : 'Test Connection'}
            </Button>

            {/* Simulation Toggle */}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.useSimulation}
                    onChange={(e) => setFormData({ ...formData, useSimulation: e.target.checked })}
                    sx={{
                      '& .Mui-checked': { color: '#006951' },
                      '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#006951' },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Demo & Live Simulation Mode
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Emulates realistic live metrics when broker is unreachable
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: '#006951', '&:hover': { bgcolor: '#005441' } }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
